import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { sendConsultantMessage } from "./consultantService";
import {
  clearConsultantSession,
  createEmptyConsultantSession,
  loadConsultantSession,
  saveConsultantSession,
} from "./consultantPersistence";
import { useScreenContextAdapter } from "./screenContextAdapter";
import { getContextualSuggestions } from "./consultantSuggestions";

const ConsultantContext = createContext(null);

const makeMessageId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `consultant-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const userFacingError = (kind) => {
  if (kind === "state_invalid") {
    return "La conversación venció o perdió validez. Podés iniciar una nueva consulta para continuar.";
  }
  if (kind === "session_expired") {
    return "Tu sesión venció. Volvé a ingresar para continuar usando el consultor.";
  }
  if (kind === "context_forbidden") {
    return "No puedo usar el contexto seleccionado. Revisá la planta o el dispositivo e intentá nuevamente.";
  }
  if (kind === "timeout") {
    return "La consulta tardó más de lo esperado. Intentá nuevamente en unos instantes.";
  }
  if (kind === "unavailable") {
    return "El consultor no está disponible temporalmente. Intentá nuevamente en unos instantes.";
  }
  if (kind === "invalid_request") {
    return "No pude procesar esa consulta. Revisá el mensaje e intentá nuevamente.";
  }
  return "No pude completar la consulta. Intentá nuevamente.";
};

const historyFromMessages = (messages) => messages
  .filter((message) => ["user", "assistant"].includes(message.role) && message.content)
  .slice(-30)
  .map((message) => ({ role: message.role, content: message.content }));

const normalizeVisibleText = (value) => String(value || "")
  .replace(/[>*_#`]/g, "")
  .replace(/\s+/g, " ")
  .trim()
  .toLocaleLowerCase("es-AR");

export const distinctClarification = (answer, clarificationQuestion) => {
  const question = String(clarificationQuestion || "").trim();
  if (!question) return null;
  return normalizeVisibleText(answer) === normalizeVisibleText(question)
    ? null
    : question;
};

export const ConsultantProvider = ({ children, request = sendConsultantMessage }) => {
  const currentUser = useSelector((state) => state.user?.currentUser);
  const userId = currentUser?.id_user ?? currentUser?.id ?? null;
  const token = currentUser?.accessToken ?? null;
  const ownerId = userId == null ? null : String(userId);
  const { section, getCurrentContext } = useScreenContextAdapter();
  const [session, setSession] = useState(() => loadConsultantSession(ownerId));
  const ownerRef = useRef(ownerId);
  const requestGenerationRef = useRef(0);

  useEffect(() => {
    if (ownerRef.current === ownerId) return;
    requestGenerationRef.current += 1;
    ownerRef.current = ownerId;
    setSession(loadConsultantSession(ownerId));
  }, [ownerId]);

  useEffect(() => {
    if (ownerId && session.ownerId === ownerId) {
      saveConsultantSession(ownerId, session);
    }
  }, [ownerId, session]);

  const setOpen = useCallback((open) => {
    setSession((previous) => ({ ...previous, open: Boolean(open) }));
  }, []);

  const resetConversation = useCallback(() => {
    requestGenerationRef.current += 1;
    clearConsultantSession(ownerId);
    setSession(createEmptyConsultantSession(ownerId, { open: true }));
  }, [ownerId]);

  const sendMessage = useCallback(async (rawMessage, { preserveExact = false } = {}) => {
    const sourceMessage = String(rawMessage || "");
    const message = preserveExact ? sourceMessage : sourceMessage.trim();
    if (!message.trim() || !ownerId || !token || session.loading) return false;

    const generation = requestGenerationRef.current;
    const userMessage = {
      id: makeMessageId(),
      role: "user",
      content: message,
      createdAt: new Date().toISOString(),
      suggestedActions: [],
      clarificationQuestion: null,
    };
    setSession((previous) => ({
      ...previous,
      messages: [...previous.messages, userMessage],
      loading: true,
      error: null,
    }));

    const payload = {
      message,
      conversationState: session.conversationState || {},
      history: historyFromMessages(session.messages),
      context: getCurrentContext(),
      locale: navigator.language || "es-AR",
    };
    if (session.conversationId) payload.conversationId = session.conversationId;
    if (session.conversationStateToken) {
      payload.conversationStateToken = session.conversationStateToken;
    }

    try {
      const result = await request({ token, payload });
      if (requestGenerationRef.current !== generation || ownerRef.current !== ownerId) return false;
      const assistantMessage = {
        id: makeMessageId(),
        role: "assistant",
        content: result.answer,
        createdAt: new Date().toISOString(),
        suggestedActions: result.suggestedActions || [],
        reportCharts: result.reportCharts || [],
        clarificationQuestion: result.clarificationRequired
          ? distinctClarification(result.answer, result.clarificationQuestion)
          : null,
      };
      setSession((previous) => ({
        ...previous,
        messages: [...previous.messages, assistantMessage],
        conversationId: result.conversationId,
        conversationState: result.conversationState || {},
        conversationStateToken: result.conversationStateToken,
        loading: false,
        error: null,
      }));
      return true;
    } catch (error) {
      if (requestGenerationRef.current !== generation || ownerRef.current !== ownerId) return false;
      setSession((previous) => ({
        ...previous,
        conversationId: error?.kind === "state_invalid" ? null : previous.conversationId,
        conversationState: error?.kind === "state_invalid" ? {} : previous.conversationState,
        conversationStateToken: error?.kind === "state_invalid" ? null : previous.conversationStateToken,
        loading: false,
        error: userFacingError(error?.kind),
      }));
      return false;
    }
  }, [getCurrentContext, ownerId, request, session, token]);

  const dismissError = useCallback(() => {
    setSession((previous) => ({ ...previous, error: null }));
  }, []);

  const value = useMemo(() => ({
    isAvailable: Boolean(ownerId && token),
    isOpen: session.open,
    messages: session.messages,
    loading: session.loading,
    error: session.error,
    section,
    contextualSuggestions: getContextualSuggestions(section),
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen(!session.open),
    sendMessage,
    resetConversation,
    dismissError,
  }), [dismissError, ownerId, resetConversation, section, sendMessage, session, setOpen, token]);

  return <ConsultantContext.Provider value={value}>{children}</ConsultantContext.Provider>;
};

export const useConsultant = () => {
  const context = useContext(ConsultantContext);
  if (!context) throw new Error("useConsultant must be used inside ConsultantProvider");
  return context;
};

export { historyFromMessages };
